import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/kyle-drive`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const MAX_ATTEMPTS = 10;
  const POLL_INTERVAL = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setPaymentStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        setPaymentDetails(response.data);

        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
        } else if (response.data.status === 'expired') {
          setPaymentStatus('expired');
        } else if (attempts < MAX_ATTEMPTS) {
          // Continue polling
          setAttempts(attempts + 1);
          setTimeout(checkPaymentStatus, POLL_INTERVAL);
        } else {
          setPaymentStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < MAX_ATTEMPTS) {
          setAttempts(attempts + 1);
          setTimeout(checkPaymentStatus, POLL_INTERVAL);
        } else {
          setPaymentStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-white flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        {paymentStatus === 'checking' && (
          <div className="bg-white rounded-3xl p-12 text-center shadow-2xl border-2 border-[hsl(210,100%,45%)]">
            <Loader2 className="w-16 h-16 text-[hsl(210,100%,45%)] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-[hsl(210,100%,12%)] mb-4">
              Verifying Your Payment...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your booking payment.
            </p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="bg-white rounded-3xl p-12 text-center shadow-2xl border-2 border-green-300">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[hsl(210,100%,12%)] mb-4">
              Payment Successful! 🎉
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for booking your Kyle Drive experience! We'll send you a confirmation email shortly with all the details.
            </p>
            {paymentDetails && (
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 mb-6 text-left">
                <h3 className="font-semibold text-[hsl(210,100%,12%)] mb-3">Booking Details:</h3>
                <p className="text-sm text-gray-700 mb-2">
                  <span className="font-medium">Amount Paid:</span> £{(paymentDetails.amount_total / 100).toFixed(2)}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Status:</span> Confirmed
                </p>
              </div>
            )}
            <Link to="/">
              <Button className="w-full bg-gradient-to-r from-[hsl(210,100%,45%)] to-[hsl(180,80%,45%)] hover:from-[hsl(210,100%,40%)] hover:to-[hsl(180,80%,40%)] text-white py-6 text-lg shadow-xl font-semibold">
                Return to Homepage
              </Button>
            </Link>
          </div>
        )}

        {(paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') && (
          <div className="bg-white rounded-3xl p-12 text-center shadow-2xl border-2 border-red-300">
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[hsl(210,100%,12%)] mb-4">
              {paymentStatus === 'expired' ? 'Payment Session Expired' : 'Payment Verification Issue'}
            </h1>
            <p className="text-gray-600 mb-6">
              {paymentStatus === 'expired' 
                ? 'Your payment session has expired. Please try booking again.'
                : 'We encountered an issue verifying your payment. If you were charged, please contact us with your booking details.'}
            </p>
            <Link to="/">
              <Button className="w-full bg-gradient-to-r from-[hsl(210,100%,45%)] to-[hsl(180,80%,45%)] hover:from-[hsl(210,100%,40%)] hover:to-[hsl(180,80%,40%)] text-white py-6 text-lg shadow-xl font-semibold">
                Return to Homepage
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
